import React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useUserStore } from './user.store'
import UserApi from './user.api'
import { GetMemberParams, ModifyMemberValues, User } from './user.type'
import { useSelectedWorkspaceStore } from '../workspace'

export const useMe = () => {
  const { setCurrentUser } = useUserStore()
  
  const query = useQuery<User>({
    queryKey: ['user', 'me'],
    queryFn: UserApi.getMe,
  })
  
  // Set current user in store when data changes
  React.useEffect(() => {
    if (query.data) {
      setCurrentUser(query.data)
    }
  }, [query.data, setCurrentUser])
  
  return query
}

export const useGetMe = () => {
  const queryClient = useQueryClient()
  const { setCurrentUser } = useUserStore()

  return useMutation({
    mutationFn: UserApi.getMe,
    onSuccess: (data) => {
      setCurrentUser(data)
      queryClient.setQueryData(['user', 'me'], data)
    },
  })
}

export const useUpdateMe = () => {
  const queryClient = useQueryClient()
  const { setCurrentUser } = useUserStore()
  
  return useMutation({
    mutationFn: UserApi.updateMe,
    onSuccess: (data) => {
      message.success('Profile updated successfully')
      setCurrentUser(data)
      queryClient.setQueryData(['user', 'me'], data)
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    },
  })
}

export const useChangePassword = () => {
  return useMutation({
    mutationFn: UserApi.changePassword,
    onSuccess: () => {
      message.success('Password changed successfully')
    },
  })
}

export const useGetMembers = ({
  params,
  enabled = true,
}: {
  params: GetMemberParams
  enabled?: boolean
}) => {
  const workspaceId = useSelectedWorkspaceStore((state) => state.selectedWorkspace?.id)
  
  return useQuery({
    queryKey: ['user', 'members', workspaceId, params],
    queryFn: () => UserApi.getMembers(params),
    enabled: enabled && !!workspaceId,
  })
}

export const useInviteMember = () => {
  const queryClient = useQueryClient()
  const workspaceId = useSelectedWorkspaceStore((state) => state.selectedWorkspace?.id)

  return useMutation({
    mutationFn: (data: Required<ModifyMemberValues>) => UserApi.inviteMember(workspaceId, data),
    onSuccess: () => {
      message.success('Invite member successfully')
      queryClient.invalidateQueries({
        queryKey: ['user', 'members'],
      })
    },
  })
}

export const useUpdateMember = () => {
  const queryClient = useQueryClient()
  const workspaceId = useSelectedWorkspaceStore((state) => state.selectedWorkspace?.id)

  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: { name?: string; role?: string } }) =>
      UserApi.updateMember(id, { ...data, workspace_id: workspaceId }),
    onSuccess: () => {
      message.success('Update member successfully')
      queryClient.invalidateQueries({
        queryKey: ['user', 'members'],
      })
    },
  })
}

export const useRemoveMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => UserApi.removeMember(id),
    onSuccess: () => {
      message.success('Remove member successfully')
      queryClient.invalidateQueries({
        queryKey: ['user', 'members'],
      })
    },
  })
}

export const useGetAssignInfo = (params: {
  workspace_id?: string
  from?: string | null
  to?: string | null
  stage?: string
}) => {
  return useQuery({
    queryKey: ['user', 'assign-info', params],
    queryFn: () => UserApi.getAssignInfo(params as any),
    enabled: !!(params.workspace_id && params.from && params.to && params.stage),
  })
}

export const useAssignManual = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: UserApi.assignManual,
    onSuccess: () => {
      message.success('Assignment successful')
      // Invalidate conversation list
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      // Invalidate conversation detail to show updated assignment
      queryClient.invalidateQueries({ queryKey: ['qa-chat-detail'] })
      // Invalidate status counts
      queryClient.invalidateQueries({ queryKey: ['qa-chat-statuses'] })
    },
  })
}

export const useGetAllMembers = (workspaceId?: string) => {
  return useQuery({
    queryKey: ['user', 'all-members', workspaceId],
    queryFn: () => UserApi.getAllMembers(workspaceId),
    enabled: !!workspaceId,
    staleTime: Infinity,
  })
}
